import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, useEffect, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  getAliveMap,
  MAPLIBRE_PICKED_ADDRESS_PIN_COLOR,
  MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH,
  MapLibreStationPinMarker,
} from "@ryde/controls";
import { ICoordinate, MarkerIconType } from "@ryde/shared";

import { AddressBlockMapStyled } from "./address-block-map.styled";
import { GOOGLE_MAPS_ZOOM } from "./constant";

interface IAddressBlockMapProps {
  coordinate: ICoordinate | undefined;
  readonly?: boolean;
  /** Fired by a click anywhere on the map and by dropping the marker — both mean "the address moved". */
  onLocationChange?: (coordinate: ICoordinate) => void;
}

/**
 * The map half of the address picker: one marker on the chosen address, moved by clicking the map or by
 * dragging it, with the caller reverse-geocoding whatever comes back.
 *
 * The pin is the app's own "picked address" marker, the one the passengers map drops on a search result —
 * Google's default red teardrop was an SDK asset and left with it.
 */
export const AddressBlockMap: FC<IAddressBlockMapProps> = ({
  coordinate,
  readonly,
  onLocationChange,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  /*
   * The Google map took `center` as a controlled prop and followed it. MapLibre's is only an initial
   * value, so following the coordinate has to be explicit.
   *
   * Gated on the map being loaded, not just on the coordinate: the form supplies the saved address on the
   * render after mount, which is before the map exists. Ungated, that first `easeTo` hit a null ref and
   * did nothing — leaving the map on the default centre with the pin far off-screen, until editing the
   * address ran the effect again against a map that was by then ready.
   */
  useEffect(() => {
    if (isMapLoaded && coordinate) {
      getAliveMap(mapRef.current)?.easeTo({ center: coordinate, duration: 0 });
    }
  }, [coordinate, isMapLoaded]);

  // Clicking the map and dropping the pin are the same intent: the address moved here.
  const handleLocationPicked = useEvent((picked: ICoordinate): void =>
    onLocationChange?.(picked),
  );

  /*
   * A marker click reaches the map underneath, whose handler treats any click as "the address moved" and
   * fires a reverse-geocode. Google's marker swallowed its own clicks, so picking the pin up did not
   * relocate the address to wherever the pointer happened to land on it.
   */
  const handleMarkerClick = useEvent(
    (event: { originalEvent: MouseEvent }): void =>
      event.originalEvent.stopPropagation(),
  );

  return (
    <AddressBlockMapStyled
      mapRef={mapRef}
      onLoadedChange={setIsMapLoaded}
      initialCenter={coordinate}
      initialZoom={GOOGLE_MAPS_ZOOM}
      onMapClick={readonly ? undefined : handleLocationPicked}
      // Google rendered its default UI here, fullscreen button included.
      isFullscreenEnabled
    >
      {isMapLoaded && coordinate && (
        <MapLibreStationPinMarker
          latitude={coordinate.lat}
          longitude={coordinate.lng}
          type={MarkerIconType.Point}
          color={MAPLIBRE_PICKED_ADDRESS_PIN_COLOR}
          label={MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH}
          onClick={handleMarkerClick}
          isDraggable={!readonly}
          onDragEnd={readonly ? undefined : handleLocationPicked}
        />
      )}
    </AddressBlockMapStyled>
  );
};
